// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useApprovals } from './useApprovals';
import { useActiveSite } from './useActiveSite';
import { HITL_APPROVAL_TYPE } from '../utils/constants';
import { oneMonthAgoStartOfDayIso } from '../utils/time';

/**
 * The single source of truth for a site's plan approvals.
 *
 * Every consumer — the rail pending badge, the Recommendations screen, and the
 * Optimizations screen — calls this so they all land on one react-query cache
 * entry (`['hitl-approvals', siteId, PlanApproval_v1, <one-month window>]`) and
 * therefore one in-flight request and one poll. The window is
 * `oneMonthAgoStartOfDayIso` (day-stable) rather than a per-mount `useMemo`, so
 * independently-mounted consumers compute the *same* key instead of forking a
 * cache entry each on sub-second `createdFrom` differences.
 *
 * Fetching is gated on a resolved `siteId`: approvals load once the active site
 * is known, and the empty pre-sites state never fires a `site_id`-less request.
 */
export function useSiteApprovals() {
  const { siteId } = useActiveSite();
  return useApprovals({
    siteId,
    approvalType: HITL_APPROVAL_TYPE,
    createdFrom: oneMonthAgoStartOfDayIso(),
    enabled: !!siteId,
  });
}
